import {
  Component,
  ElementRef,
  OnInit,
  input,
  inject,
  viewChild,
  computed,
  signal,
  effect,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isInputWithMapping, ToolInput } from '../../models/toolchain.models';
import { DataType, isValue, Value } from '../../models/tool.models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { hasValue, toArray } from '../../helper';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { NgClass } from '@angular/common';
import { selectionManager } from '../../selection';

export type ListOption = {
  value: DataType | DataType[];
  initValue: DataType | DataType[];
  isOriginal: boolean;
};

@Component({
  selector: 'app-value-input',
  templateUrl: './value-input.component.html',
  styleUrls: ['./value-input.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggle,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatIcon,
    MatCheckbox,
    MatIconButton,
    MatTooltip,
    MatDivider,
    MatButton,
    NgClass,
  ],
})
export class ValueInputComponent implements OnInit {
  readonly input = input.required<ToolInput>();
  readonly form = input.required<FormGroup>();

  /** Forward to the template. */
  protected readonly Array = Array;

  /** Inject services. */
  private readonly snackBar = inject(MatSnackBar);

  /** The input parameter. */
  readonly inputParam = computed<Value>(() => {
    const param = this.input().param;
    if (isValue(param)) {
      return param;
    }
    throw new Error('Incorrect param type.');
  });

  /** True if the param is a list. */
  readonly isListParam = computed(() => this.input().isList && this.input().isModifiable);

  readonly newEntryInput = viewChild<ElementRef>('addField');

  useMapping = computed(() => isInputWithMapping(this.input()));
  stringOptions = computed(() => {
    const input = this.input();
    if (isInputWithMapping(input)) {
      const data = input.valueToMap;
      if (Array.isArray(data)) {
        return data.map(val => val.toString());
      }
      return [data.toString()];
    }
    return [];
  });
  filteredOptions = computed(() => {
    let filterValue = '';
    const value = this.form().value;
    const input = this.input();
    if (Object.hasOwn(value, input.name)) {
      const rawValue = value[input.name];
      if (Array.isArray(rawValue)) {
        filterValue = rawValue.map(val => String(val).toLowerCase()).join(',');
      } else if (rawValue !== null && rawValue !== undefined) {
        filterValue = String(rawValue).toLowerCase();
      }
    }
    return this.stringOptions().filter(option => option?.toLowerCase().includes(filterValue));
  });

  // used to see whether a value is editable or not
  collapseList = signal(true);
  list = selectionManager<ListOption>([], []);
  editValue = signal<DataType>('');
  addValue = signal<DataType>('');
  editSelection = signal<ListOption | undefined>(undefined);

  maxListItems = 99;

  constructor() {
    effect(() => {
      const options = this.list.selected().map(val => val.value);
      this.form().controls[this.input().name]?.setValue(this.input().isList ? options : options[0]);
    });
  }

  ngOnInit() {
    this.resetOptions();
  }

  resetOptions() {
    let source: DataType | DataType[] = [];
    const input = this.input();
    if (isInputWithMapping(input)) {
      source = input.valueToMap;
    } else if (hasValue(input.param.value)) {
      source = input.param.value!;
    }
    const initValues = toArray(source);

    // Set options for editing and selection mode
    this.list.setOptions([]);
    const rawOptions = new Set(isValue(input.param) ? input.param.options : []);
    for (const init of initValues) rawOptions.add(init);
    for (const rawOption of rawOptions.values()) {
      const option = { value: rawOption, initValue: rawOption, isOriginal: true };
      this.list.addOption(option, initValues.includes(rawOption));
    }

    if (isValue(input.param)) {
      if (isInputWithMapping(input)) {
        // Add mapping value to select Options for correct display
        input.param.options = structuredClone(initValues);
      }
    }
  }

  /** Method to add value changes to correct variable **/
  handleInputChange(event: Event, mode: 'edit' | 'add') {
    if (event.target) {
      const val = (event.target as HTMLInputElement).value;
      if (mode == 'edit') {
        this.editValue.set(val);
      } else {
        this.addValue.set(val);
      }
    }
  }

  isEmpty(value: DataType) {
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    return value === null || value === undefined;
  }

  handleAddNewItem() {
    if (this.list.selectionCount() >= this.maxListItems) {
      this.snackBar.open('Cannot add more items. Maximum amount of entries reached.', 'Ok', {
        duration: 2000,
      });
      return;
    }
    if (!this.isEmpty(this.addValue())) {
      const newOption = {
        value: this.addValue(),
        initValue: this.addValue(),
        isOriginal: false,
      };
      this.list.addOption(newOption);
      this.addValue.set('');
      // reset UI field
      const input = this.newEntryInput();
      if (input) input.nativeElement.value = '';
    } else {
      this.snackBar.open('Cannot add empty items.', 'Ok', { duration: 2000 });
    }
  }

  changeOptionValue(index: number) {
    if (this.editValue() != '') {
      const oldValue = this.list.options()[index];
      this.list.updateOption(oldValue, { ...oldValue, value: this.editValue() });
      this.editValue.set('');
    }
    if (this.editSelection()) this.editSelection.set(undefined);
    else this.editSelection.set(this.list.options()[index]);
  }

  removeOption(event: Event, option: ListOption) {
    this.list.removeOption(option);
    event.stopPropagation();
  }
}
